import { useState, useEffect, useCallback } from 'react';
import { useHttpClient, useTranslation } from '@wix/fe-essentials-standalone';
import {
  ACTIONS,
  useTemplateContext,
} from '@components/Contexts/TemplateContext';
import { useTemplateBi } from '@components/Hooks/useTemplateBi';
import {
  MODAL_ACTIONS,
  useModalsContext,
} from '@components/Contexts/ModalsContext';
import { ReviewData } from '@common/types';
import {
  TEMPLATE_DELETE_REVIEW_REPLY_API_URL,
  TEMPLATE_POST_REVIEW_REPLY_API_URL,
} from '@utils/constants';
import _ from 'lodash';
import { IDeleteReply } from '@components/Reviews/types';

export const useTemplateReviewsLogic = (isMobileView: boolean) => {
  const httpClient = useHttpClient();
  const { t } = useTranslation();
  const { logBiClick } = useTemplateBi({ is_mobile: isMobileView });
  const {
    dispatch,
    template,
    reviewsInfo,
    loadNewReview,
    isEligibleToGiveReview,
    userAlreadyLeftReview,
    userReviewForTemplate,
  } = useTemplateContext();

  const { dispatchModal } = useModalsContext();
  const [showErrorNotification, setShowErrorNotification] = useState(false);

  const showReviewBanner =
    !template?.isOwnerView &&
    Boolean(isEligibleToGiveReview) &&
    !Boolean(userAlreadyLeftReview);

  // Modal and error handling
  const showModalError = useCallback(() => {
    dispatchModal({
      type: MODAL_ACTIONS.SHOW_ERROR_TOAST,
      message: t(
        'marketplace.template-page.review.error-user-already-reviewed',
      ),
    });
  }, [dispatchModal, t]);

  const openReviewModal = useCallback(
    (openUsingBanner = false) => {
      if (openUsingBanner) {
        dispatch({
          type: ACTIONS.REVIEW_MODAL_AUTO_FOCUS,
          reviewModalAutoFocus: true,
        });
      }
      dispatchModal({
        type: MODAL_ACTIONS.SHOW_REVIEW_MODAL,
        showReviewModal: true,
      });
    },
    [dispatch, dispatchModal],
  );

  const onSubmitSuccess = useCallback(
    (review: ReviewData) => {
      if (userAlreadyLeftReview) {
        dispatch({
          type: ACTIONS.DELETE_REVIEW,
          payload: review,
        });
      }
      dispatch({
        type: ACTIONS.LOAD_NEW_REVIEW,
        loadNewReview: true,
      });
      setTimeout(
        () =>
          dispatch({
            type: ACTIONS.ADD_REVIEW,
            review,
          }),
        1000,
      );
    },
    [dispatch, userAlreadyLeftReview],
  );

  const handleBiGenericClick = useCallback(
    async ({
      clickType,
      clickData,
    }: {
      clickType: string;
      clickData?: any;
    }) => {
      logBiClick({ clickType, click_data: clickData });
    },
    [logBiClick],
  );

  const deleteReply: IDeleteReply = useCallback(
    async (id: string) => {
      const deleteReplyResponse = await httpClient.post<any>(
        TEMPLATE_DELETE_REVIEW_REPLY_API_URL,
        { reviewId: id },
      );
      dispatch({
        type: ACTIONS.UPDATE_REVIEW,
        payload: deleteReplyResponse.data,
      });
    },
    [httpClient, dispatch],
  );

  const writeReplyToReview = useCallback(
    async (id: string, replyBody: string) => {
      const writeReplyReviewResponse = await httpClient.post<any>(
        TEMPLATE_POST_REVIEW_REPLY_API_URL,
        {
          reply: replyBody,
          reviewId: id,
        },
      );
      const updatedReview = writeReplyReviewResponse.data;
      dispatch({
        type: ACTIONS.UPDATE_REVIEW,
        payload: updatedReview,
      });
      await handleBiGenericClick({
        clickType: 'Submit Reply',
        clickData: { replay: replyBody },
      });
    },
    [dispatch, httpClient, handleBiGenericClick],
  );

  const onEdit = useCallback(() => {
    dispatchModal({
      type: MODAL_ACTIONS.SHOW_REVIEW_MODAL,
      showReviewModal: true,
    });
  }, [dispatchModal]);

  // Effects
  useEffect(() => {
    const hasOpenModalParam = new URLSearchParams(window.location.search).has(
      'showModal',
    );
    if (hasOpenModalParam && userAlreadyLeftReview) {
      showModalError();
    } else if (hasOpenModalParam && showReviewBanner) {
      openReviewModal();
    }
  }, [
    userAlreadyLeftReview,
    showReviewBanner,
    showModalError,
    openReviewModal,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reviewIdFromParam = params?.get('reviewId');

    if (reviewIdFromParam) {
      const review = _.find(reviewsInfo?.reviews, { id: reviewIdFromParam });
      setShowErrorNotification(!review);
    }
  }, [reviewsInfo]);

  return {
    // Data
    template,
    reviewsInfo,
    loadNewReview,
    userReviewForTemplate,
    showErrorNotification,
    showReviewBanner,

    // Functions
    openReviewModal,
    onSubmitSuccess,
    handleBiGenericClick,
    deleteReply,
    writeReplyToReview,
    onEdit,
    setShowErrorNotification,

    // Dispatch for modal close
    dispatch,
  };
};
